import { ApiStatusToFileStatus } from "@utils/constants";
import { nonNullable, unwrapConnection } from "@utils/utils";
import React from "react";
import { useNavigate } from "react-router-dom";

import {
  ApiLearnerFileFileKindChoices,
  ApiLearnerFileStatusChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ApiReportTemplateFileUploadConfigChoices,
  AssessmentToolType,
  ReportPageLocationEnums,
  ReportTypeEnums,
  useAssessmentToolsQuery,
} from "@api/graphql/types-and-hooks";
import { REASONS_TO_BE_OPEN, ValidationNoticeDialog } from "@components/ValidationNoticeDialog/ValidationNoticeDialog";
import { notifyError } from "@components/notifications/notifications";
import {
  FilterSectionSubRoute,
  useGetFilteredPageSectionsByLocation,
} from "@pages/EvaluationDetails/Components/helper";
import { Assessments } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/Assessments/Assessments";
import { BehavioralObservations } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/BehavioralObservations";
import { CatalightIntakeFiles } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/CatalightIntakeFiles";
import { ClinicalNotes } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/ClinicalNotes";
import { ConfirmGenerationDialog } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/ConfirmGenerationDialog/ConfirmGenerationDialog";
import { Interviews } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/Interviews/Interviews";
import { FooterButtonRow } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/FooterButtonRow";
import { useReport } from "@providers/ReportProvider";

export const EvaluationUploadFiles: React.FC = () => {
  const { report, reportQuery } = useReport();
  const go = useNavigate();

  const files = unwrapConnection(report?.reportFiles);
  const fileUploadConfig = report?.reportTemplate?.fileUploadConfig;
  const isCatalightTemplate = fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();
  const isKuwaitTemplate = fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.AbcKuwait.valueOf();

  const stillProcessing = files?.some(
    (f) =>
      f?.status == ApiLearnerFileStatusChoices.Processing ||
      f?.status == ApiLearnerFileStatusChoices.Pending ||
      f?.status == ApiLearnerFileStatusChoices.Classifying,
  );
  const { refetch } = reportQuery;
  const response = useAssessmentToolsQuery({ reportType: ReportTypeEnums.DiagnosisEvaluation });
  const supportedAssessmentsList: AssessmentToolType[] =
    response.data?.assessmentTools?.filter((item) => !!item).filter((tool) => tool.parsingSupported) ?? [];

  const filesForAssessment = files?.filter((file) => file?.status !== "PENDING")?.filter(nonNullable);

  const existingInterviews = filesForAssessment?.filter(
    (file) => file.fileKind === ApiLearnerFileFileKindChoices.Interview,
  );

  const existingAssessments = filesForAssessment?.filter((file) =>
    supportedAssessmentsList.map((a) => a.id).includes(file.fileKind),
  );

  const existingClinicalNotes = filesForAssessment?.filter((file) =>
    [ApiLearnerFileFileKindChoices.ClinicalNotes].includes(file.fileKind),
  );

  const existingBehavioralObservations = filesForAssessment?.filter((file) =>
    [ApiLearnerFileFileKindChoices.BehavioralObservations].includes(file.fileKind),
  );

  const existingCatalightIntakeFiles = filesForAssessment?.filter((file) =>
    [ApiLearnerFileFileKindChoices.CatalightIntake].includes(file.fileKind),
  );

  const [promptDialogOpen, setPromptDialogOpen] = React.useState<boolean>(false);
  const [validationDialogOpen, setValidationDialogOpen] = React.useState<REASONS_TO_BE_OPEN | null>(null);

  const nextPage: FilterSectionSubRoute = "report-part-1";
  const nextPageFilteredSections = useGetFilteredPageSectionsByLocation(nextPage);

  const nextPageAlreadyGenerated = nextPageFilteredSections.some(
    (section) =>
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending ||
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
  );

  const handlePrimaryButtonAction = async () => {
    if (nextPageAlreadyGenerated) {
      go(`../${nextPage}`);
      return;
    }
    try {
      const updatedFiles = (await refetch()).data?.getReports?.edges?.[0]?.node?.reportFiles.edges?.map(
        (edge) => edge?.node,
      );
      const filesForAssessment = updatedFiles?.filter((file) => file?.status !== "PENDING")?.filter(nonNullable);

      const existingAssessments = filesForAssessment?.filter((file) =>
        supportedAssessmentsList.map((a) => a.id).includes(file.fileKind),
      );

      const processingFiles = existingAssessments?.filter(
        (file) =>
          file.status === ApiLearnerFileStatusChoices.Processing ||
          file.status === ApiLearnerFileStatusChoices.Classifying,
      );

      if (processingFiles && processingFiles.length > 0) {
        setValidationDialogOpen("FILES_VALIDATING");
        return;
      }

      setPromptDialogOpen(true);
    } catch (error) {
      console.error("Assessment file retrieval error: ", error);
      notifyError("Assessment file retrieval error");
    }
  };

  return (
    <div className="flex w-full flex-col gap-y-8 ">
      <ValidationNoticeDialog reasonToBeOpen={validationDialogOpen} closeDialog={() => setValidationDialogOpen(null)} />
      <ConfirmGenerationDialog
        type={ReportPageLocationEnums.ReportPart_1}
        promptDialogOpen={promptDialogOpen}
        setPromptDialogOpen={setPromptDialogOpen}
      />

      <>
        <Interviews
          existingInterviews={
            existingInterviews?.map((existingInterview) => ({
              fileName: existingInterview.originalFileName || "<missing-file-name>",
              fileSize: existingInterview.fileSize,
              fileStatus: ApiStatusToFileStatus[existingInterview.status || ApiLearnerFileStatusChoices.Pending],
              metadata: existingInterview.metadata,
              fileKind: existingInterview.fileKind || "<missing-file-kind>",
              fileId: existingInterview.id,
              failureReason: existingInterview.processingFailureReason || "",
            })) || []
          }
        />
        {isCatalightTemplate ? (
          <CatalightIntakeFiles
            existingCatalightIntakeFiles={
              existingCatalightIntakeFiles?.map((a) => ({
                fileName: a.originalFileName,
                fileSize: a.fileSize,
                fileStatus: ApiStatusToFileStatus[a?.status || ApiLearnerFileStatusChoices.Pending],
                metadata: a.metadata,
                fileKind: a.fileKind,
                fileId: a.id,
                failureReason: a.processingFailureReason || "",
              })) || []
            }
          />
        ) : null}
        {!isKuwaitTemplate ? (
          <BehavioralObservations
            existingBehavioralObservations={
              existingBehavioralObservations?.map((a) => ({
                fileName: a.originalFileName,
                fileSize: a.fileSize,
                fileStatus: ApiStatusToFileStatus[a?.status || ApiLearnerFileStatusChoices.Pending],
                metadata: a.metadata,
                fileKind: a.fileKind,
                fileId: a.id,
                failureReason: a.processingFailureReason || "",
              })) || []
            }
          />
        ) : null}
        {!isKuwaitTemplate ? (
          <Assessments
            existingAssessments={
              existingAssessments?.map((a) => ({
                fileName: a.originalFileName,
                fileSize: a.fileSize,
                fileStatus: ApiStatusToFileStatus[a?.status || ApiLearnerFileStatusChoices.Pending],
                metadata: a.metadata,
                fileKind: a.fileKind,
                fileId: a.id,
                assessmentTemplateType: a.assessmentTemplateType,
                failureReason: a.processingFailureReason ?? "",
                category: supportedAssessmentsList.find((sa) => sa.id === a.fileKind)?.categories?.[0] || "",
              })) || []
            }
          />
        ) : null}
        {!isKuwaitTemplate ? (
          <ClinicalNotes
            existingClinicalNotes={
              existingClinicalNotes?.map((a) => ({
                fileName: a.originalFileName,
                fileSize: a.fileSize,
                fileStatus: ApiStatusToFileStatus[a?.status || ApiLearnerFileStatusChoices.Pending],
                metadata: a.metadata,
                fileKind: a.fileKind,
                fileId: a.id,
                failureReason: a.processingFailureReason || "",
              })) || []
            }
          />
        ) : null}
      </>

      <FooterButtonRow
        primaryButtonTitle="Next"
        primaryButtonDisabled={reportQuery.isFetching || stillProcessing}
        primaryButtonDisabledTooltipReason={
          stillProcessing
            ? "Please wait for all uploads to finish processing before continuing."
            : reportQuery.isFetching
              ? "Please wait while data is loading."
              : undefined
        }
        primaryButtonAction={handlePrimaryButtonAction}
        skipNavigate
      />
    </div>
  );
};
